import { DecimalPipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { switchMap, of, tap } from 'rxjs';
import { CatalogProduct } from '../../core/models/catalog.model';
import { CartService } from '../../core/services/cart.service';
import { CatalogService } from '../../core/services/catalog.service';
import { OverlayService } from '../../core/services/overlay.service';
import { isPropsHtml, normalizeCatalogText } from '../../core/utils/catalog-text.util';
import { salePercent } from '../../core/utils/money.util';
import { ProductCardComponent } from '../../shared/product-card/product-card.component';

@Component({
  selector: 'app-product-detail',
  imports: [RouterLink, DecimalPipe, ProductCardComponent],
  templateUrl: './product-detail.component.html',
  styles: `
    :host .single-product-content > .product-tabs-wrapper,
    :host .single-product-content > .container { padding-top: 40px; }
  `
})
export class ProductDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly catalog = inject(CatalogService);
  private readonly cart = inject(CartService);
  private readonly overlay = inject(OverlayService);

  readonly product = signal<CatalogProduct | null>(null);
  readonly related = signal<CatalogProduct[]>([]);
  readonly loading = signal(true);
  readonly selectedImage = signal<string | null>(null);
  readonly quantity = signal(1);
  readonly selectedVariantId = signal<string | null>(null);
  readonly selectedOptionValues = signal<Record<string, string>>({});

  readonly activeVariants = computed(() => {
    const product = this.product();
    if (!product?.variants?.length) return [];
    return product.variants.filter((v) => v.isActive).sort((a, b) => a.position - b.position);
  });

  readonly selectedVariant = computed(() => {
    const variants = this.activeVariants();
    if (!variants.length) return null;
    const id = this.selectedVariantId();
    return variants.find((v) => v.id === id) ?? variants[0];
  });

  readonly displayPrice = computed(
    () => this.selectedVariant()?.price ?? this.product()?.price ?? 0
  );

  readonly mainImage = computed(() => this.selectedImage() || this.product()?.imageUrl || '');
  readonly salePercent = salePercent;
  readonly specsHtml = computed(() => {
    const product = this.product();
    if (!product) {
      return '';
    }
    const short = normalizeCatalogText(product.shortDescription);
    if (short) {
      return short;
    }
    const description = normalizeCatalogText(product.description);
    return isPropsHtml(description) ? description : '';
  });
  readonly properties = computed(() => parsePropertyLines(this.specsHtml()));
  readonly longHtml = computed(() => {
    const product = this.product();
    if (!product) {
      return '';
    }
    const description = normalizeCatalogText(product.description);
    const specs = this.specsHtml();
    if (!description || description === specs) {
      return '';
    }
    return description;
  });
  constructor() {
    this.route.paramMap
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.related.set([]);
        }),
        switchMap((params) => {
          const slug = params.get('slug');
          if (!slug) {
            return of(null);
          }
          return this.catalog.bySlug(slug);
        }),
        takeUntilDestroyed()
      )
      .subscribe((product) => {
        this.product.set(product);
        this.selectedImage.set(product?.images[0] ?? product?.imageUrl ?? null);
        this.quantity.set(1);
        const first = (product?.variants ?? [])
          .filter((v) => v.isActive)
          .sort((a, b) => a.position - b.position)[0];
        this.selectedVariantId.set(first?.id ?? null);
        const selected: Record<string, string> = {};
        if (first?.optionValueId && product?.options) {
          for (const option of product.options) {
            if (option.values.some((v) => v.id === first.optionValueId)) {
              selected[option.id] = first.optionValueId;
            }
          }
        }
        this.selectedOptionValues.set(selected);
        this.related.set(product?.relatedProducts ?? []);
        this.loading.set(false);
      });
  }

  selectImage(url: string): void {
    this.selectedImage.set(url);
  }

  increment(): void {
    this.quantity.update((value) => value + 1);
  }

  decrement(): void {
    this.quantity.update((value) => Math.max(1, value - 1));
  }

  add(): void {
    const product = this.product();
    if (!product) {
      return;
    }
    this.cart.add(product, this.quantity(), this.selectedVariant()?.id);
    this.overlay.openCart();
  }

  selectOptionValue(optionId: string, valueId: string): void {
    const product = this.product();
    if (!product) return;
    this.selectedOptionValues.set({ [optionId]: valueId });
    const match = this.activeVariants().find((v) => v.optionValueId === valueId);
    if (match) {
      this.selectedVariantId.set(match.id);
    }
  }

  addRelated(product: CatalogProduct): void {
    this.cart.add(product);
    this.overlay.openCart();
  }
}

function parsePropertyLines(html: string): string[] {
  const value = normalizeCatalogText(html);
  if (!value.trim().toLowerCase().startsWith('<ul')) {
    return [];
  }
  return [...value.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)]
    .map((match) => match[1].replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim())
    .filter(Boolean);
}
